import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import CountdownTimer from '../shared/CountdownTimer';
import Leaderboard from '../shared/Leaderboard';
import ResultsChart from '../shared/ResultsChart';
import type { Session, Quiz, Participant } from '../../types';
import { api, useQuery, useMutation } from '../../lib/convex';
import type { Id } from '../../lib/convex';

interface SessionControllerProps {
  session: Session & { _id: Id<"sessions"> };
  quiz: Quiz;
  participants: Participant[];
  onNextQuestion: () => void;
  onPreviousQuestion: () => void;
  onEndSession: () => void;
}

const SessionController: React.FC<SessionControllerProps> = ({
  session,
  quiz,
  participants,
  onNextQuestion,
  onPreviousQuestion,
  onEndSession,
}) => {
  const [showAnswers, setShowAnswers] = useState(false);
  const [timerRunning, setTimerRunning] = useState(false);

  const showAnswersMutation = useMutation(api.sessions.showAnswers);

  const answerResults = useQuery(api.answers.getQuestionResults, {
    sessionId: session._id,
    questionIndex: session.currentQuestionIndex,
  });

  const currentQuestion = quiz?.questions?.[session.currentQuestionIndex];
  const isLastQuestion = session.currentQuestionIndex === (quiz?.questions?.length ?? 0) - 1;
  const isFirstQuestion = session.currentQuestionIndex === 0;
  
  useEffect(() => {
    setShowAnswers(false);
    setTimerRunning(true);
  }, [session.currentQuestionIndex, answerResults, currentQuestion?.options]);

  useEffect(() => {
    if (
      answerResults && 
      answerResults.totalAnswers === participants.length &&
      participants.length > 0 &&
      !showAnswers &&
      timerRunning
    ) {
      handleShowAnswers();
    }
  }, [answerResults?.totalAnswers, participants.length, showAnswers, timerRunning]);
  
  const handleShowAnswers = async () => {
    setShowAnswers(true);
    setTimerRunning(false);
    await showAnswersMutation({ id: session._id });
  };
  
  const handleTimeUp = async () => {
    setTimerRunning(false);
    await handleShowAnswers();
  };
  
  // Find index of correct answer(s)
  const correctOptionIndex = currentQuestion?.options?.findIndex(opt => opt.isCorrect) ?? -1;
  
  // If there's no current question, show a loading state
  if (!currentQuestion || !quiz) {
    return (
      <div className="grid place-items-center h-[50vh]">
        <p className="text-lg text-muted-foreground">Loading question...</p>
      </div>
    );
  }
  
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Main quiz display (will be shown on projector) */}
      <div className="lg:col-span-2">
        <Card className="w-full">
          <CardHeader>
            <div className="flex justify-between items-center mb-4">
              <CardTitle className="text-2xl">{quiz?.title}</CardTitle>
              {timerRunning && currentQuestion?.timeLimit && (
                <CountdownTimer 
                  timeLimit={currentQuestion.timeLimit} 
                  onTimeUp={handleTimeUp}
                  startTime={session.questionStartTime}
                />
              )}
            </div>
            <div className="flex justify-between items-center text-sm">
              <div className="font-semibold">
                Question {session.currentQuestionIndex + 1} of {quiz?.questions?.length ?? 0}
              </div>
              <div className="text-muted-foreground">
                {answerResults?.totalAnswers || 0} / {participants.length} answered
              </div>
            </div>
          </CardHeader>
          
          <CardContent>
            <div className="text-3xl font-heading font-bold mb-8 text-center">
              {currentQuestion?.text}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {currentQuestion?.options?.map((option, index) => (
                <div 
                  key={index}
                  className={`p-6 rounded-2xl border-2 transition-all ${
                    showAnswers && option.isCorrect 
                      ? 'bg-green-50 border-green-500' 
                      : 'bg-white border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-heading font-bold">
                        {String.fromCharCode(65 + index)}
                      </span>
                      <span className="text-lg">{option.text}</span>
                    </div>
                    {showAnswers && option.isCorrect && (
                      <span className="text-2xl text-green-600">✓</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {showAnswers && (
              <div className="mt-8">
                <h3 className="text-lg font-heading font-bold mb-4">Answer Distribution</h3>
                {answerResults === undefined ? (
                  <p className="text-muted-foreground">Loading results...</p>
                ) : (
                  <ResultsChart 
                    answerCounts={answerResults.optionCounts}
                    correctOptionIndex={correctOptionIndex}
                  />
                )}
              </div>
            )}
          </CardContent>
          
          <CardFooter className="justify-between border-t pt-4">
            <div className="flex gap-2">
              <Button 
                onClick={onPreviousQuestion} 
                disabled={isFirstQuestion || timerRunning}
                variant="outline"
              >
                Previous
              </Button>
              
              {!showAnswers ? (
                <Button onClick={handleShowAnswers}>
                  Show Answers
                </Button>
              ) : (
                <Button 
                  onClick={onNextQuestion}
                  disabled={isLastQuestion}
                >
                  Next Question
                </Button>
              )}
            </div>
            
            <Button 
              onClick={onEndSession}
              variant="destructive"
            >
              End Session
            </Button>
          </CardFooter>
        </Card>
      </div>
      
      {/* Participants and leaderboard */}
      <div>
        <Card className="w-full">
          <CardHeader>
            <CardTitle>
              Participants ({participants.length})
            </CardTitle>
            <div className="text-sm">
              Session Code: <span className="font-heading font-bold text-lg">{session.code}</span>
            </div>
          </CardHeader>
          
          <CardContent>
            <Leaderboard participants={participants} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SessionController;